import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, FormLabel, Typography } from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { resolveStrapiMediaUrl, uploadStrapiFile } from '../../utils/strapiUpload';

type BlogContentEditorProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  minHeight?: number;
  uploadErrorText?: string;
};

const BlogContentEditor: React.FC<BlogContentEditorProps> = ({
  value,
  onChange,
  label,
  minHeight = 320,
  uploadErrorText = 'Image upload failed. Try again.',
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      setUploadError('');
      setUploadingImage(true);
      try {
        const uploaded = await uploadStrapiFile(file);
        if (!uploaded) {
          setUploadError(uploadErrorText);
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const range = editor.getSelection(true);
        const index = range?.index ?? editor.getLength();
        editor.insertEmbed(index, 'image', resolveStrapiMediaUrl(uploaded.url));
        editor.setSelection(index + 1);
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  }, [uploadErrorText]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
          [{ align: [] }],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'link',
    'image',
    'align',
  ];

  return (
    <Box>
      {label && (
        <FormLabel sx={{ display: 'block', mb: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
          {label}
        </FormLabel>
      )}
      <Box
        sx={{
          position: 'relative',
          '& .ql-toolbar.ql-snow': {
            borderColor: 'divider',
            borderRadius: '4px 4px 0 0',
            bgcolor: 'background.paper',
          },
          '& .ql-container.ql-snow': {
            borderColor: 'divider',
            borderRadius: '0 0 4px 4px',
            fontFamily: 'inherit',
            fontSize: '1rem',
          },
          '& .ql-editor': {
            minHeight: minHeight - 42,
          },
          '& .ql-editor img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
          },
        }}
      >
        {uploadingImage && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.35)',
              borderRadius: 1,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
        />
      </Box>
      {uploadError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {uploadError}
        </Typography>
      )}
    </Box>
  );
};

export default BlogContentEditor;
